import React, { useState, useEffect } from 'react';
import { userAPI } from '../services/api';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiShield, FiUser, FiCheck, FiRefreshCw } from 'react-icons/fi';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// All configurable modules — groups mirror the sidebar sections exactly
const MODULES = [
  // ── Core / Projects ──────────────────────────────────────────────────────
  { key: 'dashboard',        label: 'Dashboard',          group: 'Core' },
  { key: 'projects',         label: 'Projects',           group: 'Core' },
  { key: 'tasks',            label: 'Tasks',              group: 'Core' },
  { key: 'daily_work',       label: 'Daily Work',         group: 'Core' },
  { key: 'task_templates',   label: 'Task Templates',     group: 'Core' },

  // ── HR & People ───────────────────────────────────────────────────────────
  { key: 'team',             label: 'Team',               group: 'HR & People' },
  { key: 'hr',               label: 'HR Overview',        group: 'HR & People' },
  { key: 'attendance',       label: 'Attendance',         group: 'HR & People' },
  { key: 'leaves',           label: 'Leaves',             group: 'HR & People' },
  { key: 'salary',           label: 'Salary',             group: 'HR & People' },
  { key: 'holidays',         label: 'Holidays',           group: 'HR & People' },
  { key: 'performance',      label: 'Performance',        group: 'HR & People' },
  { key: 'onboarding',       label: 'Onboarding',         group: 'HR & People' },
  { key: 'training',         label: 'Training',           group: 'HR & People' },
  { key: 'shifts',           label: 'Shifts',             group: 'HR & People' },

  // ── Time & Work ───────────────────────────────────────────────────────────
  { key: 'time_tracking',    label: 'Time Tracking',      group: 'Time & Work' },
  { key: 'daily_reports',    label: 'Daily Reports',      group: 'Time & Work' },
  { key: 'wfh',              label: 'WFH Requests',       group: 'Time & Work' },
  { key: 'workload',         label: 'Workload',           group: 'Time & Work' },
  { key: 'time_approval',    label: 'Time Approval',      group: 'Time & Work' },
  { key: 'templates',        label: 'Project Templates',  group: 'Time & Work' },
  { key: 'calendar',         label: 'Calendar',           group: 'Time & Work' },

  // ── Finance & CRM ─────────────────────────────────────────────────────────
  { key: 'analytics',        label: 'Analytics',          group: 'Finance & CRM' },
  { key: 'profit_analytics', label: 'Profit Analytics',   group: 'Finance & CRM' },
  { key: 'revenue_forecast', label: 'Revenue Forecast',   group: 'Finance & CRM' },
  { key: 'invoices',         label: 'Invoices',           group: 'Finance & CRM' },
  { key: 'payment_accounts', label: 'Payment Accounts',   group: 'Finance & CRM' },
  { key: 'expenses',         label: 'Expenses',           group: 'Finance & CRM' },
  { key: 'employee_accounts',label: 'Employee Accounts',  group: 'Finance & CRM' },
  { key: 'clients',          label: 'Clients',            group: 'Finance & CRM' },
  { key: 'crm',              label: 'CRM Pipeline',       group: 'Finance & CRM' },
  { key: 'upwork',           label: 'Upwork',             group: 'Finance & CRM' },

  // ── General ───────────────────────────────────────────────────────────────
  { key: 'messages',         label: 'Messages',           group: 'General' },
  { key: 'notifications',    label: 'Notifications',      group: 'General' },
  { key: 'permissions',      label: 'Permissions',        group: 'General' },
  { key: 'audit_log',        label: 'Audit Log',          group: 'General' },
  { key: 'settings',         label: 'Settings',           group: 'General' },
];

const ALL_PERMS = MODULES.map(m => m.key);

const DEFAULT_PERMS = ['dashboard','projects','tasks','attendance','leaves','time_tracking','daily_reports','wfh','calendar','shifts','onboarding','training','messages','notifications','settings'];
const DEFAULT_MANAGER_PERMS = ALL_PERMS;

const GROUPS = [...new Set(MODULES.map(m => m.group))];

const S = {
  page:  { fontFamily:"'Sora',sans-serif", padding:'28px 32px', minHeight:'100vh', background:'#f8fafc' },
  title: { fontSize:28, fontWeight:800, color:'#0f172a', margin:0 },
  sub:   { fontSize:13, color:'#64748b', margin:'4px 0 0' },
  card:  { background:'#fff', borderRadius:14, border:'1px solid #e2e8f0', boxShadow:'0 2px 12px #0000000a' },
  btn:   (v) => ({ padding:'8px 18px', borderRadius:9, border:'none', fontWeight:600, fontSize:13, cursor:'pointer',
    ...(v==='primary'?{background:'var(--pm-gradient)',color:'#fff'}:
       v==='danger'?{background:'#fee2e2',color:'#ef4444'}:
       {background:'#f1f5f9',color:'#64748b'}) }),
};

export default function Permissions() {
  const [members, setMembers]   = useState([]);
  const [selected, setSelected] = useState(null); // selected user id
  const [perms, setPerms]       = useState([]);    // current perm array being edited
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    userAPI.getAll().then(r => {
      const team = (r.data.users || []).filter(u => u.role === 'team' || u.role === 'manager');
      setMembers(team);
      if (team.length) selectMember(team[0]);
    }).catch(() => toast.error('Failed to load members')).finally(() => setLoading(false));
  }, []);

  // Safely parse permissions — DB may return a JSON string or array
  const parsePerms = (raw, defaultPerms) => {
    if (!raw) return [...defaultPerms];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try { const p = JSON.parse(raw); return Array.isArray(p) ? p : [...defaultPerms]; }
      catch { return [...defaultPerms]; }
    }
    return [...defaultPerms];
  };

  const selectMember = (m) => {
    setSelected(m._id || m.id);
    const defaultForRole = m.role === 'manager' ? DEFAULT_MANAGER_PERMS : DEFAULT_PERMS;
    setPerms(parsePerms(m.permissions, defaultForRole));
  };

  const toggle = (key) => {
    setPerms(prev => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.includes(key) ? arr.filter(k => k !== key) : [...arr, key];
    });
  };

  const selectAll = () => setPerms(MODULES.map(m => m.key));
  const resetDefault = () => setPerms(selectedMember?.role === 'manager' ? [...DEFAULT_MANAGER_PERMS] : [...DEFAULT_PERMS]);
  const clearAll = () => setPerms([]);

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/users/${selected}/permissions`, { permissions: perms }, { headers: headers() });
      // Update local member list
      setMembers(prev => prev.map(m => (m._id||m.id) === selected ? { ...m, permissions: perms } : m));
      toast.success('Permissions saved!');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const selectedMember = members.find(m => (m._id||m.id) === selected);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
      <div style={{ width:40, height:40, border:'3px solid #e2e8f0', borderTop:'3px solid #6366f1', borderRadius:'50%', animation:'spin 1s linear infinite' }}/>
    </div>
  );

  return (
    <div style={S.page}>
      <div style={{ marginBottom:28 }}>
        <h1 style={S.title}>Permissions</h1>
        <p style={S.sub}>Control which modules each team member can access</p>
      </div>

      {members.length === 0 ? (
        <div style={{ ...S.card, padding:60, textAlign:'center', color:'#94a3b8' }}>
          No team members found. Add team members first.
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'260px 1fr', gap:20, alignItems:'start' }}>

          {/* Member list */}
          <div style={S.card}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid #f1f5f9', fontSize:13, fontWeight:700, color:'#475569', textTransform:'uppercase', letterSpacing:0.5 }}>
              Team Members
            </div>
            {members.map(m => {
              const id = m._id || m.id;
              const isSelected = id === selected;
              const hasCustom = !!m.permissions;
              return (
                <div key={id} onClick={() => selectMember(m)} style={{
                  display:'flex', alignItems:'center', gap:12, padding:'14px 20px', cursor:'pointer',
                  background: isSelected ? '#ede9fe' : 'transparent',
                  borderLeft: isSelected ? '3px solid #6366f1' : '3px solid transparent',
                  transition:'all .15s',
                }}>
                  <div style={{ width:36, height:36, borderRadius:'50%', background: isSelected ? 'var(--pm-primary)' : '#e2e8f0', color: isSelected ? '#fff' : '#64748b', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:15, flexShrink:0 }}>
                    {m.name?.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:600, fontSize:14, color: isSelected ? '#4338ca' : '#0f172a', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{m.name}</div>
                    <div style={{ fontSize:11, color:'#94a3b8' }}>{m.position || m.email}</div>
                  </div>
                  {hasCustom && (
                    <span style={{ fontSize:9, background:'#dbeafe', color:'#1e40af', padding:'2px 6px', borderRadius:4, fontWeight:700, flexShrink:0 }}>CUSTOM</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Permission matrix */}
          {selectedMember && (
            <div style={S.card}>
              {/* Header */}
              <div style={{ padding:'20px 24px', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ width:42, height:42, borderRadius:'50%', background:'var(--pm-gradient)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:18 }}>
                    {selectedMember.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight:700, fontSize:16, color:'#0f172a' }}>{selectedMember.name}</div>
                    <div style={{ fontSize:12, color:'#64748b' }}>{selectedMember.email}</div>
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <button style={S.btn('ghost')} onClick={selectAll}>Select All</button>
                  <button style={S.btn('ghost')} onClick={resetDefault}><FiRefreshCw size={12}/> Reset Default</button>
                  <button style={S.btn('danger')} onClick={clearAll}>Clear All</button>
                  <button style={S.btn('primary')} onClick={save} disabled={saving}>
                    <FiCheck size={13}/> {saving ? 'Saving…' : 'Save Permissions'}
                  </button>
                </div>
              </div>

              {/* Module groups */}
              <div style={{ padding:24, display:'flex', flexDirection:'column', gap:24 }}>
                {GROUPS.map(group => (
                  <div key={group}>
                    <div style={{ fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:0.8, marginBottom:12 }}>
                      {group}
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:10 }}>
                      {MODULES.filter(m => m.group === group).map(mod => {
                        const enabled = Array.isArray(perms) && perms.includes(mod.key);
                        return (
                          <div key={mod.key} onClick={() => toggle(mod.key)} style={{
                            display:'flex', alignItems:'center', gap:12, padding:'12px 16px',
                            borderRadius:10, border:`2px solid ${enabled ? 'var(--pm-primary)' : '#e2e8f0'}`,
                            background: enabled ? '#ede9fe' : '#f8fafc',
                            cursor:'pointer', transition:'all .15s', userSelect:'none',
                          }}>
                            <div style={{
                              width:20, height:20, borderRadius:5, flexShrink:0,
                              background: enabled ? 'var(--pm-primary)' : '#e2e8f0',
                              display:'flex', alignItems:'center', justifyContent:'center',
                              transition:'background .15s',
                            }}>
                              {enabled && <FiCheck size={12} color="#fff" strokeWidth={3}/>}
                            </div>
                            <span style={{ fontSize:13, fontWeight: enabled ? 600 : 400, color: enabled ? '#4338ca' : '#475569' }}>
                              {mod.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer summary */}
              <div style={{ padding:'14px 24px', borderTop:'1px solid #f1f5f9', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <span style={{ fontSize:13, color:'#64748b' }}>
                  <strong style={{ color:'var(--pm-primary)' }}>{perms.length}</strong> of {MODULES.length} modules enabled
                </span>
                <button style={S.btn('primary')} onClick={save} disabled={saving}>
                  <FiCheck size={13}/> {saving ? 'Saving…' : 'Save Permissions'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
